import React, { cloneElement } from 'react';
import { useInView } from 'react-intersection-observer';

import { CardMeta } from 'components/card-meta/CardMeta';
import { Link } from 'components/link/Link';
import { Placeholder } from 'components/placeholder/Placeholder';
import { PlaceholderColor  } from 'types/placeholderColors';

import s from './Card.scss';

interface IProps {
  image?: React.ReactElement<any> | null;
  heading: string;
  subheading?: string;
  to?: string;
  date?: string;
  aspectRatio?: 'landscape' | 'square' | 'portrait';
  children?: React.ReactNode;
  active?: boolean | null | undefined;
  className?: string;
  articleType?: string;
  color?: PlaceholderColor;
  hideSubheadingInTablet?: boolean;
}

export const Card = ({
  image,
  active,
  heading,
  subheading,
  children,
  date,
  color,
  to,
  aspectRatio,
  className,
  hideSubheadingInTablet,
}: IProps) => {
  if (!heading) {
    return null;
  }

  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  // allow parent to set active state, revert to component level otherwise
  const isActive = active === undefined || active === null ? inView : inView && active;

  return (
    <div
      ref={ref}
      className={s('card', aspectRatio, className, {
        setAspectRatio: aspectRatio !== undefined,
        active: isActive,
        hasPlaceholder: !image,
      })}
    >
      <div className={s.card__imageWrap}>
        {image ? (
          cloneElement(image, {
            className: s.card__image,
          })
        ) : (
          <Link to={to}>
            <Placeholder heading={heading} active={inView} color={color}/>
          </Link>
        )}
      </div>

      <div className={s.card__content}>
        {image && image !== null && (
          <h2 className={s.card__heading}>
            {to ? (
              <Link className={s.card__link} to={to}>
                {heading}
              </Link>
            ) : (
              heading
            )}
          </h2>
        )}

        {subheading && (
          <div className={s('card__subheading',
          { hiddenTablet: hideSubheadingInTablet})}>{subheading}
        </div>)}

        {date && (
          <div className={s.card__meta}>
            <CardMeta date={date} />
          </div>
        )}

        {children && <div className={s.card__children}>{children}</div>}
      </div>
    </div>
  );
};
